import { loginRedirectSearch } from "@/lib/login-redirect";
import { portalSlugFromPath } from "@/lib/portal-slug";

/** A 32-byte hex credential, as the API's `authtoken.Generate` spells it. */
export const ACCEPT_INVITE_TOKEN_RE = /^[a-f0-9]{64}$/;

export interface AcceptInviteCtaTargets {
  /** The landing's own URL, token included — what every exit carries back. */
  landingHref: string;
  /** The utility's sign-in, returning to the landing once signed in. */
  signIn: { to: "/portal/$slug"; params: { slug: string }; search: { redirect: string } };
}

/** Where a utility's mailed account-portal invitation lands. */
function landingPath(slug: string): string {
  return `/portal/${slug}/accept-invite`;
}

/**
 * The exits the accept-invite landing offers, and the landing URL they carry
 * back. This exists because the landing builds its sign-in exit from a bare
 * slug and token rather than from a router location: there is no
 * `location.href` to hand `loginRedirectSearch` until someone spells the
 * landing's own URL, and the token is the visit's entire payload. Spelling it
 * is all this owns — the `?redirect=` half is `loginRedirectSearch`'s,
 * unchanged.
 */
export function acceptInviteCtaTargets(slug: string, token: string): AcceptInviteCtaTargets {
  const landingHref = `${landingPath(slug)}?${new URLSearchParams({ token }).toString()}`;
  return {
    landingHref,
    signIn: {
      to: "/portal/$slug",
      params: { slug },
      search: loginRedirectSearch({ href: landingHref }),
    },
  };
}

/**
 * The landing href when `location` IS a utility's landing with a well-formed
 * slug and token, null anywhere else. The auth-failure teardown reads this to
 * decide whether a dead session goes back to the link — where the token is the
 * visit's whole payload — or to /login. Rebuilt from the slug and token rather
 * than echoed, so the answer is always the canonical landing.
 */
export function acceptInviteLandingAt(location: {
  pathname: string;
  search: string;
}): string | null {
  const slug = portalSlugFromPath(location.pathname);
  if (slug === null || location.pathname !== landingPath(slug)) return null;
  const token = new URLSearchParams(location.search).get("token");
  if (token === null || !ACCEPT_INVITE_TOKEN_RE.test(token)) return null;
  return acceptInviteCtaTargets(slug, token).landingHref;
}
